import React, { useState } from 'react'
import { Link, Outlet } from 'react-router-dom'
import {
  FaTachometerAlt,
  FaRegFileImage,
  FaNewspaper,
  FaHandshake,
  FaChartLine,
  FaMoneyCheck,
  FaGlobeAmericas,
  FaFlask,
  FaCreditCard,
  FaUsers,
  FaBars,
  FaChevronRight,
} from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import UserDropdown from '../../components/UserDropdown'
import { useAuth } from '../../contexts/AuthContext'

const Sidebar = () => {
  // const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isSlider, setIsSlider] = useState(false)
  const [isTrendingNews, setIsTrendingNews] = useState(false)
  const [isDeal, setIsDeal] = useState(false)
  const [isInsight, setIsInsight] = useState(false)
  const [isIpo, setIsIpo] = useState(false)
  const [isWorld, setIsWorld] = useState(false)
  const [isSample, setIsSample] = useState(false)
  const [isContactus, setIsContactus] = useState(false)
  const [isPayment, setIsPayment] = useState(false)
  const [isUsers, setIsUsers] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [activeItem, setActiveItem] = useState(null)

  // Animation variants for the dropdown menus
  const dropdownVariants = {
    open: {
      opacity: 1,
      height: 'auto',
      transition: { duration: 0.3 },
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3 },
    },
  }

  // Animation variants for the sidebar
  const sidebarVariants = {
    open: {
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
    closed: {
      x: '-100%',
      transition: { type: 'spring', stiffness: 300, damping: 30 },
    },
  }

  const handleMenuItemClick = (item) => {
    setActiveItem(item)
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Top Navbar */}
      <div className="bg-[#ff7010] text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center justify-center gap-2 mr-2 sm:mr-0">
          <button
            className="text-white focus:outline-none"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <FaBars size={22} />
          </button>
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="DEAL STREET JOURNAL"
              className="w-auto h-8 sm:h-10 lg:h-14"
            />
          </div>
        </div>
        <div className="flex items-center">
          <UserDropdown />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden text-sm font-aptos-regular">
        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={sidebarVariants}
              className="bg-white text-gray-500 w-60 overflow-y-auto shadow-lg z-10"
            >
              <nav className="mt-4">
                <ul>
                  {/* Dashboard Section- visible to all authenticated users */}
                  <li
                    className={`p-4 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                      activeItem === 'dashboard' ? 'text-black font-medium' : ''
                    }`}
                    onClick={() => handleMenuItemClick('dashboard')}
                  >
                    <div className="flex items-center">
                      <FaTachometerAlt
                        className="mr-3 text-gray-400"
                        size={20}
                      />
                      <Link to="/dealdashboard" className="w-full text-left">
                        Dashboard
                      </Link>
                    </div>
                  </li>

                  {/* Collapsible Slider Section */}

                  {hasPermission('sliders') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isSlider || activeItem === 'slider'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsSlider(!isSlider)}
                      >
                        <div className="flex items-center">
                          <FaRegFileImage
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>Sliders</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isSlider ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isSlider && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'slider-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('slider-item')}
                            >
                              <Link
                                to="/dealdashboard/slider"
                                className="ml-12 text-left block"
                              >
                                Slider
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Trending News Section */}
                  {hasPermission('trendingNews') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isTrendingNews || activeItem === 'news'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsTrendingNews(!isTrendingNews)}
                      >
                        <div className="flex items-center">
                          <FaNewspaper
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>Trending News</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isTrendingNews ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isTrendingNews && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'news-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('news-item')}
                            >
                              <Link
                                to="/dealdashboard/news"
                                className="ml-12 text-left block"
                              >
                                News
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Deal Section */}
                  {(hasPermission('perseed') ||
                    hasPermission('seed') ||
                    hasPermission('growth') ||
                    hasPermission('ma') ||
                    hasPermission('graph') ||
                    hasPermission('blog')) && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isDeal || activeItem === 'deal'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsDeal(!isDeal)}
                      >
                        <div className="flex items-center">
                          <FaHandshake
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>Deal</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isDeal ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isDeal && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            {hasPermission('preseed') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'pre-seed'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('pre-seed')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/preseed"
                                    className="ml-12 text-left block"
                                  >
                                    Pre-Seed
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('seed') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'seed'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('seed')}
                                >
                                  <Link
                                    to="/dealdashboard/seed"
                                    className="ml-12 text-left block"
                                  >
                                    Seed
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('growth') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'growth'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('growth')}
                                >
                                  <Link
                                    to="/dealdashboard/growth"
                                    className="ml-12 text-left block"
                                  >
                                    Growth
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('ma') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'ma'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('ma')}
                                >
                                  <Link
                                    to="/dealdashboard/ma"
                                    className="ml-12 text-left block"
                                  >
                                    M & A
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('graph') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'graph'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('graph')}
                                >
                                  <Link
                                    to="/dealdashboard/graph"
                                    className="ml-12 text-left block font-aptos-bold"
                                  >
                                    Graph
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('blog') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'blog'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('blog')}
                                >
                                  <Link
                                    to="/dealdashboard/blog"
                                    className="ml-12 text-left block"
                                  >
                                    Opinion
                                  </Link>
                                </li>
                              </>
                            )}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible DSJ Insight Section */}
                  {(hasPermission('latestDeal') ||
                    hasPermission('funding365') ||
                    hasPermission('financial') ||
                    hasPermission('customiseReport') ||
                    true) && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isInsight || activeItem === 'insight'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsInsight(!isInsight)}
                      >
                        <div className="flex items-center">
                          <FaChartLine
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>DSJ Insight</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isInsight ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isInsight && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            {hasPermission('latestDeal') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'latest-deal'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('latest-deal')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/latestDeal"
                                    className="ml-12 text-left block"
                                  >
                                    Latest Deal
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('funding365') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'funding-365'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('funding-365')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/funding"
                                    className="ml-12 text-left block"
                                  >
                                    Funding 365
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('financial') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'financial-report'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('financial-report')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/financial"
                                    className="ml-12 text-left block"
                                  >
                                    Financial Report
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('customiseReport') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'custom-report'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('custom-report')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/customise"
                                    className="ml-12 text-left block"
                                  >
                                    Customise Report
                                  </Link>
                                </li>
                              </>
                            )}

                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible IPO Section */}
                  {hasPermission('ipo') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isIpo || activeItem === 'ipo'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsIpo(!isIpo)}
                      >
                        <div className="flex items-center">
                          <FaMoneyCheck
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>IPO</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isIpo ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isIpo && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'ipo-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('ipo-item')}
                            >
                              <Link
                                to="/dealdashboard/ipo"
                                className="ml-12 text-left block"
                              >
                                IPO
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible World Section */}
                  {hasPermission('world') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isWorld || activeItem === 'world'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsWorld(!isWorld)}
                      >
                        <div className="flex items-center">
                          <FaGlobeAmericas
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>World</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isWorld ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isWorld && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'world-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('world-item')}
                            >
                              <Link
                                to="/dealdashboard/world"
                                className="ml-12 text-left block"
                              >
                                World
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Trending News Section */}
                  {hasPermission('contactus') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isTrendingNews || activeItem === 'news'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsContactus(!isContactus)}
                      >
                        <div className="flex items-center">
                          <FaNewspaper
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>Contact-Us</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isContactus ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isContactus && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'news-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() =>
                                handleMenuItemClick('contactus-item')
                              }
                            >
                              <Link
                                to="/dealdashboard/contactus"
                                className="ml-12 text-left block"
                              >
                                Message
                              </Link>
                            </li>

                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'news-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() =>
                                handleMenuItemClick('contactus-item')
                              }
                            >
                              <Link
                                to="/dealdashboard/founder"
                                className="ml-12 text-left block"
                              >
                                Founder
                              </Link>
                            </li>

                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'news-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() =>
                                handleMenuItemClick('contactus-item')
                              }
                            >
                              <Link
                                to="/dealdashboard/investor"
                                className="ml-12 text-left block"
                              >
                                Investor
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Sample Section */}
                  {hasPermission('sampleReport') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isSample || activeItem === 'sample'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsSample(!isSample)}
                      >
                        <div className="flex items-center">
                          <FaFlask className="mr-3 text-gray-400" size={18} />
                          <span>Sample</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isSample ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isSample && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'sample-item'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('sample-item')}
                            >
                              <Link
                                to="/dealdashboard/sample"
                                className="ml-12 text-left block"
                              >
                                Sample
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Payment Section */}
                  {(hasPermission('paymentStatus') ||
                    hasPermission('productDeliver') ||
                    hasPermission('invoice')) && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isPayment || activeItem === 'payment'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsPayment(!isPayment)}
                      >
                        <div className="flex items-center">
                          <FaCreditCard
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>Payment</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isPayment ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isPayment && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            {hasPermission('paymentStatus') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'payment-status'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('payment-status')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/payment-status"
                                    className="ml-12 text-left block"
                                  >
                                    Payment Status
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('productDeliver') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'product-delivered'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('product-delivered')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/product-delivered"
                                    className="ml-12 text-left block"
                                  >
                                    Product Delivered
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('invoice') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'invoice'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('invoice')}
                                >
                                  <Link
                                    to="/dealdashboard/invoice"
                                    className="ml-12 text-left block"
                                  >
                                    Invoice
                                  </Link>
                                </li>
                              </>
                            )}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible Users Section */}
                  {(hasPermission('users') ||
                    hasPermission('userCart') ||
                    hasPermission('userActivity')) && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isUsers || activeItem === 'users'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsUsers(!isUsers)}
                      >
                        <div className="flex items-center">
                          <FaUsers className="mr-3 text-gray-400" size={18} />
                          <span>All Users</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isUsers ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isUsers && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            {hasPermission('users') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'user'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() => handleMenuItemClick('user')}
                                >
                                  <Link
                                    to="/dealdashboard/users"
                                    className="ml-12 text-left block"
                                  >
                                    User
                                  </Link>
                                </li>
                              </>
                            )}
                            {hasPermission('userCart') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'user-cart'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('user-cart')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/user/cart"
                                    className="ml-12 text-left block"
                                  >
                                    Cart
                                  </Link>
                                </li>
                              </>
                            )}

                            {hasPermission('userActivity') && (
                              <>
                                <li
                                  className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                    activeItem === 'user-activity'
                                      ? 'text-black font-medium'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleMenuItemClick('user-activity')
                                  }
                                >
                                  <Link
                                    to="/dealdashboard/user-activity"
                                    className="ml-12 text-left block"
                                  >
                                    User Activity
                                  </Link>
                                </li>
                              </>
                            )}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}

                  {/* Collapsible World Section */}
                  {hasPermission('admin') && (
                    <>
                      <li
                        className={`p-4 uppercase cursor-pointer hover:bg-gray-100 hover:text-gray-700 flex items-center justify-between ${
                          isAdmin || activeItem === 'world'
                            ? 'text-black font-medium'
                            : ''
                        }`}
                        onClick={() => setIsAdmin(!isAdmin)}
                      >
                        <div className="flex items-center">
                          <FaGlobeAmericas
                            className="mr-3 text-gray-400"
                            size={18}
                          />
                          <span>All Admin</span>
                        </div>
                        <FaChevronRight
                          className={`transform transition-transform ${
                            isAdmin ? 'rotate-90' : ''
                          }`}
                        />
                      </li>
                      <AnimatePresence>
                        {isAdmin && (
                          <motion.ul
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={dropdownVariants}
                            className="w-full overflow-hidden bg-gray-50"
                          >
                            <li
                              className={`p-3 hover:bg-gray-100 hover:text-gray-700 ${
                                activeItem === 'admin'
                                  ? 'text-black font-medium'
                                  : ''
                              }`}
                              onClick={() => handleMenuItemClick('admin')}
                            >
                              <Link
                                to="/dealdashboard/admin"
                                className="ml-12 text-left block"
                              >
                                admin
                              </Link>
                            </li>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </ul>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content Section */}
        <div className="flex-1 p-4 overflow-y-auto bg-[#F2F5F7]">
          <Outlet /> {/* This is where the nested routes will be rendered */}
        </div>
      </div>
    </div>
  )
}

export default Sidebar
